import React, { useEffect, useState, useMemo, useCallback } from "react";
import Spinner from "../../skills-experience/shared/spinner";
import { useMsal } from "@azure/msal-react";
import ApiProvider from "../../../apiProvider";
import { CdsContentPanel } from "clayco-component-library";
import "../../../styles/contacts.scss";
import ContactModal from "./contact-modal";
import ContactTable from "./contact-table";
import { ConfirmDlg } from "../../skills-experience/shared/confirm-dlg";
import EmployeeLookup from "../../../Components/EmployeeLookup/EmployeeLookup";
import useVisibilityStore from "../../../store/visibilityStore";
import { useEmployeeNumber } from "../../../employeeNumber";

const Contacts = ({ setMenuOptions }) => {
    const { instance, accounts } = useMsal();
    // Stable api provider instance for hooks
    const apiProvider = useMemo(() => new ApiProvider(instance, accounts[0]), [instance, accounts]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [contact, setContact] = useState(null);
    const [contacts, setContacts] = useState(null); // data shown in unified table (current user or OBO)
    const [relationships, setRelationships] = useState([]);
    const [tooManyContacts, setTooManyContacts] = useState(false);
    const [update, setUpdate] = useState(false);
    // Verification (audit) state
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditInfo, setAuditInfo] = useState(null); // { employeeNumber, lastChangedUtc }
    const [canVerify, setCanVerify] = useState(true); // whether to show the verify button
    // Admin panel state
    // Determine contact admin privileges from new visibility rule LoadContactAdmin
    const { visibilityRules } = useVisibilityStore();
    const { employeeNumber } = useEmployeeNumber();
    const isContactAdmin = !!(visibilityRules?.LoadContactAdmin || visibilityRules?.loadContactAdmin);
    const isEmergencyContactViewer = !!(visibilityRules?.EmergencyContactViewer || visibilityRules?.emergencyContactViewer);
    const hasAdminAccess = isContactAdmin || isEmergencyContactViewer;
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [adminLookupLoading, setAdminLookupLoading] = useState(false);
    const [adminLookupError, setAdminLookupError] = useState(null);
    // remove separate adminContactData; we will reuse `contacts`

    // Memoized panel description to avoid re-renders
    const adminPanelDescription = useMemo(() => (
        <>
            {isContactAdmin && 'Find employees and update their emergency contact information.'}
            {isEmergencyContactViewer && !isContactAdmin && 'Find employees and view their emergency contact information. You can only modify your own contacts.'}
        </>
    ), [isContactAdmin, isEmergencyContactViewer]);

    const fetchEmployeeContacts = useCallback(async (empNumber) => {
        if(!empNumber) return; // when cleared we fall back to current user via getContacts
        setAdminLookupLoading(true);
        setAdminLookupError(null);
        // Reset too many contacts warning when initiating a lookup for a new employee.
        // It will be recalculated after data is fetched.
        setTooManyContacts(false);
        try {
            const result = await apiProvider.Get(`Profile/GetEmergencyContactsForUser/${empNumber}`, null, 'application/json');
            if(result && !result.error) {
                const processed = (Array.isArray(result) ? result : []).map(c => ({
                    ...c,
                    areaCode: c.areaCode,
                    phoneNumber: c.phoneNumber
                }));
                setContacts(processed);
                // Recompute warning based on selected employee's contacts
                setTooManyContacts(processed.length >= 3);
                setAdminLookupError(processed.length === 0 ? 'No emergency contacts found for this employee.' : null);
            } else if(result?.error) {
                setAdminLookupError(result.error);
                // Ensure warning cleared on error (can't assert they have too many)
                setTooManyContacts(false);
            } else {
                setAdminLookupError('Failed to load contacts.');
                setTooManyContacts(false);
            }
        } catch (e) {
            setAdminLookupError('An unexpected error occurred while loading contacts.');
            setTooManyContacts(false);
        } finally {
            setAdminLookupLoading(false);
        }
    }, [apiProvider]);

    useEffect(() => {
        async function getRelationships() {
            let results = await apiProvider.Get("Fields/Relationships");
            if (results && !results.error) {
                setRelationships(results);
            }
        }

        async function getCountries() {
            let result = await apiProvider.Get(
                "Fields/Countries",
                null,
                "application/json",
                false
            );
            if (result && !result.error && result !== "" && result.length !== 0) {
                // Move USA to the top of the list
            const usIndex = result.findIndex(
                (item) => item.countryId === 2 && item.countryCode === "US"
            );
            if (usIndex !== -1) {
                const spliced = result.splice(usIndex, 1);
                result.splice(0, 0, spliced[0]);
            }
                setCountries(result.map((i) => ({
                    index: i.countryId,
                    name: i.countryName,
                    ...i
                })));
            }
        }

        setLoading(true);
        getRelationships();
        setMenuOptions("Contacts");
        getContacts(true);
        getCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiProvider]);

    const getContacts = useCallback(async (updateLoading, forceUpdate = false) => {
        setLoading(true);
        try {
            let result = await apiProvider.Get("Profile/GetEmergencyContacts", null, 'application/json');
            if (result && !result.error) {
                const processedContacts = result.map(contact => ({
                    ...contact,
                    areaCode: contact.areaCode,
                    phoneNumber: contact.phoneNumber
                }));
                setTooManyContacts(processedContacts.length >= 3);
                // Update if not viewing another employee OR forced (e.g., cleared selection revert)
                if(!selectedEmployee || forceUpdate){
                    setContacts(processedContacts);
                }
                setError(null);
            } else {
                setError("Failed to load emergency contacts.");
            }
        } catch (err) {
            setError("An error occurred while fetching emergency contacts.");
        }
        setLoading(false);
    }, [apiProvider, selectedEmployee]);

    function edit(item) {
        setContact(item);
        setUpdate(true);
        setShowModal(true);
    }

    async function remove(item) {
        const proceed = await ConfirmDlg(
            "Are you sure you want to delete this emergency contact?",
            "Delete"
        );
        if (!proceed) return;
        setLoading(true);
        try {
            if(selectedEmployee?.employeeNumber){
                // Admin OBO delete
                const result = await apiProvider.Delete(`Profile/Admin/DeleteEmergencyContactForUser/${selectedEmployee.employeeNumber}`, item);
                if(result && !result.error){
                    const processed = (Array.isArray(result) ? result : []).map(c => ({
                        ...c,
                        areaCode: c.areaCode,
                        phoneNumber: c.phoneNumber
                    }));
                    setContacts(processed);
                    setError(null);
                } else if(result?.error){
                    setError(result.error);
                } else {
                    setError('Failed to delete emergency contact for selected employee.');
                }
            } else {
                let payload = { ...item, deleteContact: "Y" };
                await apiProvider.Delete("Profile/DeleteEmergencyContact", payload);
                await getContacts(true, true);
            }
        } catch(e){
            console.error('Failed to delete contact', e);
            setError('Failed to delete contact.');
        } finally {
            setLoading(false);
        }
    }

    function addContact() {
        setShowModal(true);
        setContact({});
    }

    // Restored updatePrimaryContact (was corrupted by earlier patch)
    async function updatePrimaryContact(item) {
        const id = item?.id;
        if (!id) return;
        setLoading(true);
        try {
            if(selectedEmployee?.employeeNumber){
                const result = await apiProvider.Patch(`Profile/Admin/UpdatePrimaryContactForUser/${selectedEmployee.employeeNumber}`, { id });
                if(result && !result.error){
                    const processed = (Array.isArray(result) ? result : []).map(c => ({
                        ...c,
                        areaCode: c.areaCode,
                        phoneNumber: c.phoneNumber
                    }));
                    setContacts(processed);
                    const primary = processed.find(c => c.isPrimary);
                    if(primary) setContact(primary);
                    setError(null);
                } else if(result?.error){
                    setError(result.error);
                } else {
                    setError('Failed to update primary contact for selected employee.');
                }
            } else {
                // Current user primary update with server authoritative list
                const result = await apiProvider.Patch("Profile/UpdatePrimaryContact", { id });
                if(result && !result.error){
                    const processed = (Array.isArray(result) ? result : []).map(c => ({
                        ...c,
                        areaCode: c.areaCode,
                        phoneNumber: c.phoneNumber
                    }));
                    setContacts(processed);
                    const primary = processed.find(c => c.isPrimary);
                    if(primary) setContact(primary);
                    setError(null);
                } else if(result?.error){
                    setError(result.error);
                } else {
                    setError('Failed to update primary contact.');
                }
            }
        } catch(e){
            console.error('Failed to update primary contact', e);
            setError('Failed to update primary contact.');
        } finally {
            setLoading(false);
        }
    }

    // Reintroduced saveContactInfo used by ContactModal
    const saveContactInfo = async (data) => {
        if(!data) return;
        setShowModal(false);
        setLoading(true);

        const payload = {
            id: data?.id,
            firstName: data?.firstName,
            lastName: data?.lastName,
            relationship: data?.relationship,
            phoneNumber: data?.areaCode && data?.phoneNumber ? `${data.areaCode}${data.phoneNumber}` : data?.phoneNumber,
            addressLine1: data?.address1,
            addressLine2: data?.address2,
            city: data?.city,
            state: data?.state,
            country: data?.country,
            postalCode: data?.postalCode,
            isPrimary: contacts?.length === 0 ? true : data?.isPrimary
        };

        try {
            if(selectedEmployee?.employeeNumber){
                // Admin OBO save
                const result = await apiProvider.Post(`Profile/Admin/SetEmergencyContactForUser/${selectedEmployee.employeeNumber}`, payload);
                if(result && !result.error){
                    const processed = (Array.isArray(result) ? result : []).map(c => ({
                        ...c,
                        areaCode: c.areaCode,
                        phoneNumber: c.phoneNumber
                    }));
                    setContacts(processed);
                    setError(null);
                } else if(result?.error){
                    setError(result.error);
                } else {
                    setError('Failed to save emergency contact for selected employee.');
                }
            } else {
                // Current user save
                await apiProvider.Post(`Profile/SetEmergencyContact`, payload);
                await getContacts(true, true); // force update current user list
            }
        } catch(e){
            console.error('Failed to save contact info', e);
            setError('Failed to save contact information.');
        } finally {
            setLoading(false);
        }
    };

    function descriptionBuilder() {
        return (
            <>
                <>
                    Please take a few moments to provide the information of at least <b>one</b>, and up to <b>three</b>, emergency contacts.
                </>
            </>
        );
    }

    function closeRefresh() {
        setShowModal(false);
        setUpdate(false);
        getContacts();
    }

    // Fetch existing audit information to decide if we should show verification button
    const fetchAuditInfo = useCallback(async () => {
        const loggedInEmpNumber = employeeNumber?.number;
        if(!loggedInEmpNumber) return;
        setAuditLoading(true);
        try {
            const result = await apiProvider.Get(`api/EmergencyContactReminder/Audit/${loggedInEmpNumber}`);
            if(result && !result.error){
                setAuditInfo(result);
                const lastChangedUtc = result.lastChangedUtc;
                if(lastChangedUtc){
                    const last = new Date(lastChangedUtc);
                    const now = new Date();
                    const diffDays = (now - last) / (1000 * 60 * 60 * 24);
                    setCanVerify(diffDays >= 30); // show button only if >=30 days since last change/verification
                } else {
                    // No timestamp -> allow verification
                    setCanVerify(true);
                }
            } else {
                // If error, default to allowing verification
                setCanVerify(true);
            }
        } catch(e){
            // On failure allow verification so user can attempt to record
            console.error('Failed to load contact audit info', e);
            setCanVerify(true);
        } finally {
            setAuditLoading(false);
        }
    }, [apiProvider, employeeNumber?.number]);

    // Load audit info when employee number is available
    useEffect(() => {
        fetchAuditInfo();
    }, [fetchAuditInfo]);

    // Assumption: audit endpoint expects POST with no body to record verification event.
    const auditContacts = async () => {
        setVerifyLoading(true);

        try {
            const loggedInEmpNumber = employeeNumber?.number;
            await apiProvider.Post(`api/EmergencyContactReminder/Audit/${loggedInEmpNumber}`);
            // Update local audit state to hide button immediately
            const nowUtc = new Date().toISOString();
            setAuditInfo({ employeeNumber: loggedInEmpNumber, lastChangedUtc: nowUtc });
            setCanVerify(false);

        } catch(e){
            console.error('Audit verify contacts failed', e);
        } finally {
            setVerifyLoading(false);
        }
    };

    return (
        <>
            <div>
                {hasAdminAccess && (
                    <CdsContentPanel
                        title="On-Behalf-Of Administration"
                        description={adminPanelDescription}
                        collapsible={false}
                        defaultCollapsed={false}
                        disableButtons={adminLookupLoading}
                    >
                        <div className="mb-3">
                            <EmployeeLookup
                                label="Search employee"
                                required={false}
                                onEmployeeSelected={(emp) => {
                                    if (emp) {
                                        setSelectedEmployee(emp);
                                        fetchEmployeeContacts(emp.employeeNumber);
                                    } else {
                                        // cleared selection
                                        setSelectedEmployee(null);
                                        setAdminLookupError(null);
                                        // Force update to revert to current user contacts using fresh state
                                        getContacts(true, true);
                                    }
                                }}
                            />
                        </div>
                        {adminLookupLoading && <Spinner show={true} text={"loading contacts..."} />}
                        {adminLookupError && (
                            <div className='cds-text-red mb-2'>
                                {adminLookupError}
                            </div>
                        )}
                        {selectedEmployee && !adminLookupLoading && !adminLookupError && (
                            <p className="text-muted">Showing selected employee's contacts below.</p>
                        )}
                        {!selectedEmployee && !adminLookupLoading && !adminLookupError && (
                            <p className="text-muted">Showing your contacts below.</p>
                        )}
                    </CdsContentPanel>
                )}

                <div style={{ marginTop: hasAdminAccess ? '2rem' : 0 }}>
                <CdsContentPanel
                    title="Emergency Contacts"
                    description={descriptionBuilder()}
                    primaryButtonText="Add a contact"
                    primaryButtonSubmits={false}
                    primaryButtonHandler={addContact}
                    disableButtons={loading || tooManyContacts || (isEmergencyContactViewer && selectedEmployee)}
                    collapsible={false}
                >
                    {loading ? (
                        <Spinner show={true} text={"syncing..."} />
                    ) : error ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '150px',
                            textAlign: 'center',
                            color: '#B00020',
                            backgroundColor: '#FFF4F4',
                            borderRadius: '4px',
                            padding: '1rem',
                            fontWeight: '500'
                        }}>
                            {error}
                        </div>
                    ) : (
                        <>
                            <ContactTable
                                contacts={contacts}
                                countries={countries}
                                edit={edit}
                                remove={remove}
                                setPrimary={updatePrimaryContact}
                                isEmergencyContactViewer={isEmergencyContactViewer}
                                isViewingOtherEmployee={!!selectedEmployee}
                            />
                            
                            <div>
                                <br />
                                {auditLoading && (
                                    <Spinner show={true} text={'checking verification status...'} />
                                )}
                                {!auditLoading && canVerify && contacts && contacts.length > 0 && !selectedEmployee && (
                                    <button
                                        className='btn btn-outline-primary'
                                        disabled={verifyLoading}
                                        onClick={auditContacts}
                                    >
                                        {verifyLoading ? 'Verifying...' : 'My Contacts Are Current'}
                                    </button>
                                )}
                                {!auditLoading && !canVerify && auditInfo?.lastChangedUtc && !selectedEmployee && (
                                    <div className='alert p-2'>
                                        Your emergency contacts were verified or updated on {new Date(auditInfo.lastChangedUtc).toLocaleDateString()}.
                                    </div>
                                )}
                                <hr />
                                <i>
                                    If this information changes in the future, please return to this Employee Portal and update the necessary contact(s.)
                                    This information will be treated confidentially and will only be used in emergency situations. If you have any questions, please speak with your manager or Employee Relations.
                                </i>
                            </div>
                            {tooManyContacts && (
                                <div className='me-0 ms-auto'>
                                    <p className='cds-text-red'>
                                        You have entered the maximum number of emergency contacts. If you wish to add another, please remove a contact first.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </CdsContentPanel>
                </div>

                {showModal && (
                    <ContactModal
                        show={showModal}
                        relationships={relationships}
                        data={contact}
                        countries={countries}
                        onSave={saveContactInfo}
                        onCloseModal={closeRefresh}
                        setPrimary={updatePrimaryContact}
                        update={update}
                    />
                )}
            </div>
        </>
    );
};

export default Contacts;
